import { Badge } from '@/components/ui/badge';
import type {
  ActivationEventStatus,
  ActivationStatus,
  GenerationStatus,
  DeliveryStatus,
  GenerationApprovalStatus,
} from '@/types/generated/activations.generated';

type Variant = 'default' | 'secondary' | 'destructive' | 'outline';

const EVENT_STATUS: Record<ActivationEventStatus, { label: string; variant: Variant }> = {
  Draft: { label: 'Draft', variant: 'secondary' },
  Active: { label: 'Active', variant: 'default' },
  Ended: { label: 'Ended', variant: 'outline' },
  Cancelled: { label: 'Cancelled', variant: 'destructive' },
};

const ACTIVATION_STATUS: Record<ActivationStatus, { label: string; variant: Variant }> = {
  Draft: { label: 'Draft', variant: 'secondary' },
  Live: { label: 'Live', variant: 'default' },
  Ended: { label: 'Ended', variant: 'outline' },
};

const GENERATION_STATUS: Record<GenerationStatus, { label: string; variant: Variant }> = {
  Pending: { label: 'Pending', variant: 'secondary' },
  Processing: { label: 'Processing', variant: 'default' },
  Succeeded: { label: 'Succeeded', variant: 'default' },
  Failed: { label: 'Failed', variant: 'destructive' },
};

const DELIVERY_STATUS: Record<DeliveryStatus, { label: string; variant: Variant }> = {
  Pending: { label: 'Pending', variant: 'secondary' },
  Delivered: { label: 'Delivered', variant: 'default' },
  Failed: { label: 'Failed', variant: 'destructive' },
};

const APPROVAL_STATUS: Record<GenerationApprovalStatus, { label: string; variant: Variant }> = {
  NotRequired: { label: 'No approval needed', variant: 'outline' },
  PendingApproval: { label: 'Pending approval', variant: 'secondary' },
  Approved: { label: 'Approved', variant: 'default' },
  Rejected: { label: 'Rejected', variant: 'destructive' },
};

export function EventStatusBadge({ status }: { status: ActivationEventStatus }) {
  const s = EVENT_STATUS[status];
  return <Badge variant={s.variant} data-testid="event-status-badge">{s.label}</Badge>;
}

export function ActivationStatusBadge({ status }: { status: ActivationStatus }) {
  const s = ACTIVATION_STATUS[status];
  return <Badge variant={s.variant} data-testid="activation-status-badge">{s.label}</Badge>;
}

export function GenerationStatusBadge({ status }: { status: GenerationStatus }) {
  const s = GENERATION_STATUS[status];
  return <Badge variant={s.variant} data-testid="generation-status-badge">{s.label}</Badge>;
}

export function DeliveryStatusBadge({ status }: { status: DeliveryStatus }) {
  const s = DELIVERY_STATUS[status];
  return <Badge variant={s.variant} data-testid="delivery-status-badge">{s.label}</Badge>;
}

export function ApprovalStatusBadge({ status }: { status: GenerationApprovalStatus }) {
  const s = APPROVAL_STATUS[status];
  return <Badge variant={s.variant} data-testid="approval-status-badge">{s.label}</Badge>;
}
